import {
  UnexpectedErrorAlert,
  EmailChangeSuccessAlert,
  EmailChangeSecurityNoteAlert,
  EmailChangeEmailExistsAlert,
  PasswordExpiredAlert,
  IncorrectEmailPasswordAlert,
} from '@/components/alerts';
import { EMAIL_REGEX, EMAIL_VALIDATION_MESSAGE } from '@/constants';
import type { ReactNode } from 'react';
import { SubmitButton } from '@/components/SubmitButton';
import { AccountCenterBox } from '@/layout/AccountCenterBox';
import { CustomInput } from '@/components/CustomInput';
import { PasswordInput } from '@/components/PasswordInput';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { api } from '@/services/api';
import { useAuth } from '@/auth/useAuth';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { ChangeEmailResponse, ChangeEmailResultEnum } from '@shared/types';
import { AxiosResponse } from 'axios';

enum FormStatus {
  NoAlert = 'NoAlert',
  UnexpectedError = 'UnexpectedErrorAlert',
  EmailChangeSuccess = 'EmailChangeSuccessAlert',
  EmailChangeEmailExists = 'EmailChangeEmailExistsAlert',
  PasswordExpired = 'PasswordExpiredAlert',
  IncorrectEmailPassword = 'IncorrectEmailPasswordAlert',
}

export const ChangeEmail = () => {
  useDocumentTitle('Marshall Reddick - Change Email');
  const { email: cookieEmail } = useAuth();
  const queryEmail = new URLSearchParams(window.location.search).get('email');
  const email = cookieEmail || queryEmail;

  type AlertRenderer = (context: { newEmail: string }) => ReactNode;

  const alertMap: Partial<Record<FormStatus, AlertRenderer>> = {
    [FormStatus.UnexpectedError]: () => <UnexpectedErrorAlert />,
    [FormStatus.EmailChangeSuccess]: ({ newEmail }) => <EmailChangeSuccessAlert email={newEmail} />,
    [FormStatus.EmailChangeEmailExists]: () => <EmailChangeEmailExistsAlert />,
    [FormStatus.PasswordExpired]: () => <PasswordExpiredAlert />,
    [FormStatus.IncorrectEmailPassword]: () => <IncorrectEmailPasswordAlert />,
  };

  return (
    <Formik
      initialValues={{ currentEmail: email, newEmail: '', password: '' }}
      initialStatus={FormStatus.NoAlert}
      validationSchema={Yup.object().shape({
        newEmail: Yup.string()
          .trim()
          .matches(EMAIL_REGEX, EMAIL_VALIDATION_MESSAGE)
          .transform((value) => value.trim()),
        password: Yup.string(),
      })}
      validateOnMount={false}
      onSubmit={async (values, { setFieldError, setStatus }) => {
        let hasErrors = false;
        if (!values.newEmail.trim()) {
          setFieldError('newEmail', 'New email address is required');
          hasErrors = true;
        }
        if (!values.password.trim()) {
          setFieldError('password', 'Password is required');
          hasErrors = true;
        }

        if (hasErrors) {
          return;
        }

        setStatus(FormStatus.NoAlert);

        try {
          const response: AxiosResponse<ChangeEmailResponse> = await api.post(
            '/auth/change-email-request',
            {
              currentEmail: values.currentEmail,
              newEmail: values.newEmail,
              password: values.password,
            },
          );
          const { result } = response.data;
          if (result === ChangeEmailResultEnum.FORCED_PASSWORD_RESET) {
            setStatus(FormStatus.PasswordExpired);
          } else if (result === ChangeEmailResultEnum.INVALID_CREDENTIALS) {
            setStatus(FormStatus.IncorrectEmailPassword);
          } else if (result === ChangeEmailResultEnum.EMAIL_EXISTS) {
            setStatus(FormStatus.EmailChangeEmailExists);
          } else if (result === ChangeEmailResultEnum.SUCCESS) {
            setStatus(FormStatus.EmailChangeSuccess);
          } else {
            setStatus(FormStatus.UnexpectedError);
          }
        } catch {
          setStatus(FormStatus.UnexpectedError);
        }
      }}
    >
      {({ errors, touched, isSubmitting, status, values }) => {
        const statusKey = status as FormStatus;
        const alert = alertMap[statusKey];
        return (
          <AccountCenterBox isSubmitting={isSubmitting}>
            <Form noValidate>
              {alert ? alert({ newEmail: values.newEmail }) : null}
              {statusKey !== FormStatus.EmailChangeSuccess && (
                <>
                  {/* CURRENT EMAIL */}
                  <Field
                    name="currentEmail"
                    as={CustomInput}
                    type="email"
                    label="Current Email Address"
                    error={errors.currentEmail}
                    touched={touched.currentEmail}
                    readOnly={true}
                  />
                  {/* NEW EMAIL */}
                  <Field
                    name="newEmail"
                    as={CustomInput}
                    type="email"
                    label="New Email Address"
                    error={errors.newEmail}
                    touched={touched.newEmail}
                  />
                  {/* PASSWORD */}
                  <Field
                    name="password"
                    as={PasswordInput}
                    error={errors.password}
                    touched={touched.password}
                  />
                  <EmailChangeSecurityNoteAlert />
                  {/* BUTTON */}
                  <SubmitButton
                    text="Save New Email"
                    className="mt-6"
                    isSubmitting={isSubmitting}
                    disabled={false}
                  />
                </>
              )}
            </Form>
            {statusKey === FormStatus.EmailChangeSuccess && (
              <button
                className="btn-submit cursor-pointer"
                onClick={() =>
                  (window.location.href = import.meta.env.VITE_WEBSITE_URL + '/account')
                }
              >
                <span className="inline-flex items-center gap-2">
                  Back to Profile
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
                </span>
              </button>
            )}
          </AccountCenterBox>
        );
      }}
    </Formik>
  );
};
